import ActionButton from "@/components/buttons/ActionButton";
import { InputNumber } from "@/components/InputNumber";
import { selectStandardProps } from "@/utils/componentUtils";
import { tInterval } from "@/utils/translationUtils";
import { validations } from "@/utils/validationUtils";
import { Flex, Form, Select, Space } from "antd";
import { FormInstance } from "antd/lib/form";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentAgentAccount } from "../../../../auth/hooks";
import { AgentSelect } from "../../../../enumerations/components/form/AgentSelect";
import { GenGainersData } from "../../types";
import styles from "./GainersCommissionRateDivisionForm.module.scss";

type Props = {
  form: FormInstance<GenGainersData>;
  allowOtherOption?: boolean;
};

enum CommissionRateRatio {
  _100_ = "_100_",
  _0_100_ = "_0_100_",
  _50_50_ = "_50_50_",
  _0_50_50_ = "_0_50_50_",
  OTHER = "OTHER",
}

const PREDEFINED_RATE_RATIOS: Record<
  Exclude<CommissionRateRatio, CommissionRateRatio.OTHER>,
  { gainersCount: number; rates: Pick<GenGainersData, "gainer1Rate" | "gainer2Rate" | "gainer3Rate"> }
> = {
  [CommissionRateRatio._100_]: {
    gainersCount: 1,
    rates: { gainer1Rate: 100, gainer2Rate: undefined, gainer3Rate: undefined },
  },
  [CommissionRateRatio._0_100_]: {
    gainersCount: 2,
    rates: { gainer1Rate: 0, gainer2Rate: 100, gainer3Rate: undefined },
  },
  [CommissionRateRatio._50_50_]: {
    gainersCount: 2,
    rates: { gainer1Rate: 50, gainer2Rate: 50, gainer3Rate: undefined },
  },
  [CommissionRateRatio._0_50_50_]: {
    gainersCount: 3,
    rates: { gainer1Rate: 0, gainer2Rate: 50, gainer3Rate: 50 },
  },
};

export const GainersCommissionRateDivisionForm = ({ form, allowOtherOption }: Props) => {
  const { t } = useTranslation();
  const [rateRatio, setRateRatio] = useState<CommissionRateRatio>(CommissionRateRatio._100_);
  const [gainersCount, setGainersCount] = useState(1);

  const userAccount = useCurrentAgentAccount();

  const handleRemoveGainer = (gainerNumber: number) => {
    setGainersCount(gainerNumber - 1);

    form.setFieldsValue({
      [`gainer${gainerNumber}Id` as keyof GenGainersData]: undefined,
      [`gainer${gainerNumber}Rate` as keyof GenGainersData]: undefined,
    });

    if (gainerNumber === 2) {
      form.setFieldsValue({ gainer1Rate: 100 });
    }
  };

  useEffect(() => {
    form.setFieldsValue(PREDEFINED_RATE_RATIOS[CommissionRateRatio._100_].rates);
  }, []);

  const handleRateRatioChange = (ratio: CommissionRateRatio) => {
    setRateRatio(ratio);

    if (ratio !== CommissionRateRatio.OTHER) {
      setGainersCount(PREDEFINED_RATE_RATIOS[ratio].gainersCount);
      form.setFieldsValue(PREDEFINED_RATE_RATIOS[ratio].rates);
    }
  };

  return (
    <div>
      <span style={{ paddingRight: 8 }}>{t("commissions.enums.commissionRateRatio._label")}</span>
      <Select<CommissionRateRatio>
        {...selectStandardProps}
        className={styles.rateSelect}
        value={rateRatio}
        onChange={handleRateRatioChange}
        options={Object.values(CommissionRateRatio)
          .filter((value) => allowOtherOption || value !== CommissionRateRatio.OTHER)
          .map((value) => ({
            value,
            label: t(`commissions.enums.commissionRateRatio.${value}`),
          }))}
      />
      <div className={styles.gainersBox}>
        <Form form={form} layout="vertical">
          {Array.from({ length: gainersCount }, (_, index) => index + 1).map((i) => {
            return (
              <Flex gap="middle" key={i}>
                <Form.Item
                  name={`gainer${i}Id`}
                  style={{ flex: 1 }}
                  label={tInterval(`contract.attrs.gainerRecords.gainerId_interval`, i)}
                  rules={[validations.notNull, validations.noRepeatedGainer(gainersCount)]}
                  initialValue={i === 1 && userAccount?.agent?.canBeGainer ? userAccount.agent.id : undefined}
                  dependencies={Array.from({ length: gainersCount }, (_, index) => index + 1)
                    .filter((value) => i !== value)
                    .map((value) => `gainer${value}Id`)}
                >
                  <AgentSelect optionsProps={{ onlyCanBeGainer: true }} />
                </Form.Item>
                <div style={{ flex: 1 }}>
                  {gainersCount > 1 ? (
                    <Form.Item
                      name={`gainer${i}Rate`}
                      label={t(`contract.attrs.gainerRecords.gainerRate`, { count: i })}
                      dependencies={Array.from({ length: gainersCount }, (_, index) => index + 1)
                        .filter((value) => i !== value)
                        .map((value) => `gainer${value}Rate`)}
                      rules={[
                        validations.notNull,
                        validations.minNumber(0),
                        validations.maxNumber(100),
                        validations.gainersRateSumOneHundred(gainersCount),
                      ]}
                      validateDebounce={350}
                      initialValue={i === 1 ? 100 : undefined}
                    >
                      <InputNumber
                        type="decimal"
                        addon="percentage"
                        disabled={gainersCount === 1 || rateRatio !== CommissionRateRatio.OTHER}
                      />
                    </Form.Item>
                  ) : undefined}
                </div>
              </Flex>
            );
          })}
        </Form>

        {rateRatio === CommissionRateRatio.OTHER ? (
          <Space>
            <ActionButton icon="plus" disabled={gainersCount > 2} onClick={() => setGainersCount(gainersCount + 1)}>
              {t("contract.actions.addGainer")}
            </ActionButton>
            <ActionButton icon="minus" disabled={gainersCount < 2} onClick={() => handleRemoveGainer(gainersCount)}>
              {t("contract.actions.removeGainer")}
            </ActionButton>
          </Space>
        ) : undefined}
      </div>
    </div>
  );
};
